import React from 'react';
import { followUser, unfollowUser } from '../../../services/social/api';
import './FollowButton.css';

interface FollowButtonProps {
  userId: string;
  initialFollowing: boolean;
  /** Notifies the parent so it can adjust the follower count optimistically. */
  onChange?: (following: boolean) => void;
  className?: string;
}

/**
 * Follow / unfollow toggle (T2.2). Optimistic: flips immediately, reverts on
 * error. The caller decides whether to render it (e.g. hide on your own profile).
 */
const FollowButton: React.FC<FollowButtonProps> = ({ userId, initialFollowing, onChange, className }) => {
  const [following, setFollowing] = React.useState(initialFollowing);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setFollowing(initialFollowing);
  }, [initialFollowing]);

  const toggle = async () => {
    if (busy) return;
    const next = !following;
    setBusy(true);
    setFollowing(next); // optimistic
    onChange?.(next);
    try {
      if (next) await followUser(userId);
      else await unfollowUser(userId);
    } catch (error) {
      setFollowing(!next); // revert
      onChange?.(!next);
      console.error('Follow toggle failed:', error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`v2-follow-button ${following ? 'v2-follow-button--following' : ''} ${className || ''}`}
      onClick={toggle}
      disabled={busy}
      aria-pressed={following}
    >
      {following ? 'Following' : 'Follow'}
    </button>
  );
};

export default FollowButton;
